export const SIZE_MAP = {
  Xsmall: { width: 430, height: 240 },
  Small: { width: 560, height: 315 },
  Medium: { width: 776, height: 436 },
  Large: { width: 1920, height: 1080 },
  SocialFeed: { width: '100%', height: '100%' },
};

export function getSizeAttrs(sizeKey = 'Medium') {
  return SIZE_MAP[sizeKey] || SIZE_MAP.Medium;
}

function isMp4Path(pathname) {
  return /\.mp4$/i.test(pathname || '');
}

function isSandiaGovHost(host) {
  const h = String(host || '').toLowerCase().replace(/^www\./, '');
  return h === 'sandia.gov' || h.endsWith('.sandia.gov');
}

function isSharePointHost(host) {
  const h = String(host || '').toLowerCase().replace(/^www\./, '');
  return h === 'sandialabs.sharepoint.com' || h.endsWith('.sharepoint.com');
}

export function getVideoSource(url) {
  if (!url) return null;

  try {
    const u = new URL(url);
    const host = u.hostname.replace(/^www\./, '').toLowerCase();
    const path = (u.pathname || '').toLowerCase();

    // Direct MP4s
    if (isSandiaGovHost(host) && isMp4Path(path)) return 'sandia-mp4';
    if (isSharePointHost(host) && isMp4Path(path)) return 'sharepoint-mp4';

    // Platforms
    if (host.includes('youtube.com') || host === 'youtu.be') return 'youtube';
    if (host.includes('vimeo.com')) return 'vimeo';
    if (host === 'digitalmedia.sandia.gov') return 'digitalmedia';
    if (host === 'digitalops.sandia.gov') return 'digitalops';

    return 'unknown';
  } catch {
    return null;
  }
}

export function isDigitalMediaAllowed() {
  if (typeof window === 'undefined') return true;

  const host = String(window.location?.hostname || '').toLowerCase();

  if (
    host === 'www.sandia.gov' ||
    host === 'sandia.gov' ||
    host === 'www-qual.sandia.gov' ||
    host === 'www-dev.sandia.gov' ||
    host === 'sharepoint.sandia.gov'
  )
    return false;

  return true;
}

export function getUnsupportedUrlMessage(source) {
  if (source === 'digitalmedia' && !isDigitalMediaAllowed()) {
    return 'DigitalMedia videos are not allowed on the SON (wp.sandia.gov & communicate.sandia.gov)';
  }

  return 'Only YouTube, Vimeo, Sandia DigitalMedia, Sandia DigitalOps, and direct MP4s hosted on *.sandia.gov or SharePoint are supported.';
}

export function isSupportedVideoSource(source) {
  if (source === 'digitalmedia' && !isDigitalMediaAllowed()) return false;

  return (
    source === 'youtube' ||
    source === 'vimeo' ||
    source === 'digitalmedia' ||
    source === 'digitalops' ||
    source === 'sandia-mp4' ||
    source === 'sharepoint-mp4'
  );
}

export function normalizeVideoUrl(input) {
  const raw = (input || '').trim();
  if (!raw) return '';

  const withScheme = /^www\./i.test(raw) ? `https://${raw}` : raw;

  try {
    const u = new URL(withScheme);
    const host = u.hostname.replace(/^www\./, '').toLowerCase();
    const path = u.pathname || '';

    if (host === 'digitalmedia.sandia.gov') {
      const playMatch = path.match(/\/Mediasite\/Play\/([a-f0-9]+)/i);
      if (playMatch?.[1]) {
        return `https://digitalmedia.sandia.gov/Mediasite/Play/${playMatch[1]}`;
      }

      const presMatch = path.match(/\/Presentation\/([a-f0-9]+)/i);
      if (presMatch?.[1]) {
        return `https://digitalmedia.sandia.gov/Mediasite/Play/${presMatch[1]}`;
      }
    }

    return withScheme;
  } catch {
    return withScheme;
  }
}

export function getDigitalMediaVideoCode(url) {
  if (!url) return '';

  try {
    const u = new URL(normalizeVideoUrl(url));
    const path = u.pathname || '';

    const playMatch = path.match(/\/Mediasite\/Play\/([a-f0-9]+)/i);
    if (playMatch?.[1]) return playMatch[1];

    const presMatch = path.match(/\/Presentation\/([a-f0-9]+)/i);
    if (presMatch?.[1]) return presMatch[1];

    return '';
  } catch {
    return '';
  }
}

async function fetchAuthToken(videoCode, { signal } = {}) {
  const url = `/wp-json/digitalmedia/v1/auth-token/?video_code=${encodeURIComponent(videoCode)}`;

  const res = await fetch(url, {
    credentials: 'same-origin',
    headers: { Accept: 'application/json' },
    signal,
  });

  const contentType = res.headers.get('content-type') || '';
  const bodyText = await res.text();

  let data = null;

  if (contentType.includes('application/json')) {
    try {
      data = JSON.parse(bodyText);
    } catch (e) {
      throw new Error(
        `Auth endpoint returned invalid JSON (HTTP ${res.status}). First bytes: ${bodyText.slice(
          0,
          200
        )}`
      );
    }
  } else {
    try {
      data = JSON.parse(bodyText);
    } catch {
      throw new Error(
        `Auth endpoint did not return JSON (HTTP ${res.status}, content-type: ${contentType}). First bytes: ${bodyText.slice(
          0,
          200
        )}`
      );
    }
  }

  if (!res.ok) {
    const msg = data?.message || data?.error || `HTTP ${res.status}`;
    throw new Error(`Auth token request failed: ${msg}`);
  }

  if (typeof data?.authToken !== 'string' || !data.authToken) {
    throw new Error(
      `Auth token response missing authToken string. Got: ${bodyText.slice(0, 200)}`
    );
  }

  return data.authToken;
}

export async function getEmbedUrl(url, videoCode, { signal } = {}) {
  if (!url) return '';

  const normalized = normalizeVideoUrl(url);
  const source = getVideoSource(normalized);

  if (!source || !isSupportedVideoSource(source)) return '';

  try {
    const u = new URL(normalized);

    if (source === 'digitalmedia') {
      const code = videoCode || getDigitalMediaVideoCode(normalized);
      if (!code) return '';

      const authToken = await fetchAuthToken(code, { signal });

      return `https://digitalmedia.sandia.gov/Mediasite/Play/${code}?AuthTicket=${encodeURIComponent(
        authToken
      )}&autostart=false&player=50a54cab1d3541bc913f556ea1b6df590a&playfrom=0&covertitle=false`;
    }

    if (source === 'youtube') {
      let videoId = '';

      if (u.hostname.includes('youtube.com')) {
        videoId = u.searchParams.get('v') || '';
        if (!videoId && u.pathname.startsWith('/embed/')) {
          videoId = u.pathname.split('/embed/')[1];
        }
      } else if (u.hostname === 'youtu.be') {
        videoId = u.pathname.replace('/', '');
      }

      return videoId ? `https://www.youtube.com/embed/${videoId}` : '';
    }

    if (source === 'vimeo') {
      const parts = u.pathname.split('/').filter(Boolean);
      const videoId = parts[0];
      return videoId ? `https://player.vimeo.com/video/${videoId}` : '';
    }

    if (source === 'digitalops') {
      return normalized;
    }

    if (source === 'sandia-mp4' || source === 'sharepoint-mp4') {
      return normalized;
    }

    return '';
  } catch (e) {
    console.error('Error in getEmbedUrl:', e);
    return '';
  }
}
